/**
 * AccessoryCustomizationModal - Right-side drawer for customizing an accessory
 */
import { useState, useEffect } from 'react';
import type { FaceIDType, AccessoryCapabilities } from '@infrastructure/three/prestigeService';
import { PART_TYPE_NAMES, PartType } from '@infrastructure/three/prestigeService';
import {
  CURTAIN_TEXTURES,
  FRAME_TEXTURES,
  WPC_WALL_TEXTURES,
  type TextureOption,
} from '@domain/constants/ralColors';

const FACE_NAMES: Record<FaceIDType, string> = {
  East: 'Side 1',
  West: 'Side 2',
  South0: 'Side 3',
  South1: 'Side 3',
  North0: 'Side 4',
  North1: 'Side 4',
};

/* ── Sub-components ── */

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <div
    style={{
      fontSize: 12,
      fontWeight: 700,
      color: '#666',
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      marginBottom: 8,
    }}
  >
    {children}
  </div>
);

const SliderControl = ({
  label,
  value,
  onChange,
  min = 0,
  max = 100,
  minLabel = 'Closed',
  maxLabel = 'Open',
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  minLabel?: string;
  maxLabel?: string;
}) => (
  <div>
    <SectionLabel>{label}</SectionLabel>
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 6,
      }}
    >
      <span style={{ fontSize: 12, color: '#888' }}>{minLabel}</span>
      <span style={{ fontSize: 14, fontWeight: 600, color: '#333' }}>{value}%</span>
      <span style={{ fontSize: 12, color: '#888' }}>{maxLabel}</span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      style={{
        width: '100%',
        accentColor: '#71903e',
        height: 6,
      }}
    />
  </div>
);

const ColorSwatchGrid = ({
  textures,
  selected,
  onSelect,
}: {
  textures: TextureOption[];
  selected: string;
  onSelect: (t: TextureOption) => void;
}) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
    {textures.map((t) => {
      const isActive = selected === t.img;
      return (
        <button
          key={t.img}
          type="button"
          onClick={() => onSelect(t)}
          title={t.name}
          style={{
            width: 36,
            height: 36,
            borderRadius: 8,
            background: t.hex,
            border: isActive ? '3px solid #71903e' : '2px solid #d9d9d9',
            cursor: 'pointer',
            boxSizing: 'border-box',
            transition: 'border-color 0.15s',
            padding: 0,
            position: 'relative',
          }}
        >
          {isActive && (
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%,-50%)',
              }}
            >
              <path
                d="M3 7L6 10L11 4"
                stroke="#fff"
                strokeWidth="2"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </button>
      );
    })}
  </div>
);

/* ── Props ── */

type Props = {
  faceId: FaceIDType | null;
  partType: number | null;
  capabilities: AccessoryCapabilities | null;
  mergeDoors: boolean[];
  mergeDoorCount: number;
  movementValue: number;
  secondaryMovementValue: number;
  isLeftValue: boolean;
  textureValue: string;
  textureSecondaryValue: string;
  onClose: () => void;
  onMovementChange: (value: number) => void;
  onSecondaryMovementChange: (value: number) => void;
  onLeftRightChange: (isLeft: boolean) => void;
  onMergeDoorToggle: (doorIndex: number, visible: boolean) => void;
  onTextureChange: (textureImg: string) => void;
  onTextureSecondaryChange: (textureImg: string) => void;
};

/* ── Main component ── */

export const AccessoryCustomizationModal = ({
  faceId,
  partType,
  capabilities,
  mergeDoors,
  mergeDoorCount,
  movementValue,
  secondaryMovementValue,
  isLeftValue,
  textureValue,
  textureSecondaryValue,
  onClose,
  onMovementChange,
  onSecondaryMovementChange,
  onLeftRightChange,
  onMergeDoorToggle,
  onTextureChange,
  onTextureSecondaryChange,
}: Props) => {
  const [movement, setMovement] = useState(0);
  const [secondaryMovement, setSecondaryMovement] = useState(0);
  const [isLeft, setIsLeft] = useState(true);
  const [selectedTexture, setSelectedTexture] = useState('');
  const [selectedTextureSecondary, setSelectedTextureSecondary] = useState('');

  useEffect(() => {
    setMovement(movementValue);
    setSecondaryMovement(secondaryMovementValue);
    setIsLeft(isLeftValue);
    setSelectedTexture(textureValue || '');
    setSelectedTextureSecondary(textureSecondaryValue || '');
  }, [faceId, movementValue, secondaryMovementValue, isLeftValue, textureValue, textureSecondaryValue]);

  if (!faceId || partType === null || !capabilities) return null;

  const faceName = FACE_NAMES[faceId] || faceId;
  const partName = PART_TYPE_NAMES[partType] || `Accessory ${partType}`;
  const isShutterPanel = partType === PartType.SLIDING_ADJUSTABLE_SHUTTER_PANEL;

  const handleMovement = (v: number) => { setMovement(v); onMovementChange(v); };
  const handleSecondaryMovement = (v: number) => { setSecondaryMovement(v); onSecondaryMovementChange(v); };
  const handleLeftRight = (val: 'left' | 'right') => {
    const l = val === 'left';
    setIsLeft(l);
    onLeftRightChange(l);
  };
  const handleTexture = (t: TextureOption) => { setSelectedTexture(t.img); onTextureChange(t.img); };
  const handleSecondaryTexture = (t: TextureOption) => { setSelectedTextureSecondary(t.img); onTextureSecondaryChange(t.img); };

  const getTextureList = (): TextureOption[] => {
    if (capabilities.isCurtain) return CURTAIN_TEXTURES;
    if (capabilities.isWpcWall) return WPC_WALL_TEXTURES;
    return FRAME_TEXTURES;
  };

  const getTextureTitle = (): string => {
    if (capabilities.isCurtain) return 'Fabric';
    if (isShutterPanel) return 'Louver Color';
    if (capabilities.isWpcWall || partType === PartType.WPC_FENCING_SCREEN || partType === PartType.VERTICAL_WPC_DECORATION_SLAT) return 'Finish';
    if (capabilities.isLikeGlassDoor) return 'Frame Color';
    return 'Color';
  };

  const textureList = getTextureList();
  const frameTextureList = FRAME_TEXTURES;
  const positionTitle = isShutterPanel ? 'Panel Edge' : 'Door Position';
  const positionOptions = isShutterPanel && (faceId === 'East' || faceId === 'West')
    ? (['front', 'back'] as const)
    : (['left', 'right'] as const);

  return (
    <>
      {/* Backdrop */}
      <div
        role="button"
        tabIndex={0}
        onClick={onClose}
        onKeyDown={(e) => { if (e.key === 'Escape' || e.key === 'Enter') onClose(); }}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.3)',
          zIndex: 1001,
        }}
      />

      {/* Panel — slides in from the right */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: 310,
          maxWidth: '85vw',
          background: '#fff',
          borderTopLeftRadius: 12,
          borderBottomLeftRadius: 12,
          boxShadow: '-4px 0 20px rgba(0,0,0,0.12)',
          zIndex: 1002,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            flexShrink: 0,
            padding: '16px 16px 12px',
            borderBottom: '1px solid #eee',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#333' }}>
                {partName}
              </div>
              <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>
                {faceName}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '6px 18px',
                fontSize: 13,
                fontWeight: 600,
                color: '#fff',
                background: '#71903e',
                border: 'none',
                borderRadius: 18,
                cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              Done
            </button>
          </div>
        </div>

        {/* Scrollable content */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 20,
          }}
        >
          {/* Movement */}
          {capabilities.canMove && (
            <SliderControl
              label="Open / Close"
              value={movement}
              onChange={handleMovement}
            />
          )}

          {/* Secondary movement */}
          {capabilities.canMoveSecondary && (
            <SliderControl
              label={isShutterPanel ? 'Tilt Louvers' : 'Secondary Control'}
              value={secondaryMovement}
              onChange={handleSecondaryMovement}
              minLabel={isShutterPanel ? 'Closed' : 'Min'}
              maxLabel={isShutterPanel ? 'Open' : 'Max'}
            />
          )}

          {/* Left / Right */}
          {capabilities.hasLeftRight && (
            <div>
              <SectionLabel>{positionTitle}</SectionLabel>
              <div style={{ display: 'flex', gap: 0, borderRadius: 8, overflow: 'hidden', border: '1.5px solid #e0e0e0' }}>
                {positionOptions.map((val, index) => {
                  const isActive = (index === 0) === isLeft;
                  return (
                    <button
                      key={val}
                      type="button"
                      onClick={() => handleLeftRight(index === 0 ? 'left' : 'right')}
                      style={{
                        flex: 1,
                        padding: '10px 0',
                        fontSize: 13,
                        fontWeight: isActive ? 700 : 400,
                        color: isActive ? '#fff' : '#555',
                        background: isActive ? '#71903e' : 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        textTransform: 'capitalize',
                      }}
                    >
                      {val}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Merge panel toggles */}
          {capabilities.isMergeType && mergeDoorCount > 0 && (
            <div>
              <SectionLabel>Panel ON / OFF</SectionLabel>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {Array.from({ length: mergeDoorCount }).map((_, i) => {
                  const on = mergeDoors[i] ?? true;
                  return (
                    <button
                      key={`panel-${String(i)}`}
                      type="button"
                      onClick={() => onMergeDoorToggle(i, !on)}
                      style={{
                        padding: '8px 16px',
                        fontSize: 13,
                        fontWeight: on ? 600 : 400,
                        color: on ? '#fff' : '#888',
                        background: on ? '#71903e' : '#f5f5f5',
                        border: on ? '1.5px solid #71903e' : '1.5px solid #e0e0e0',
                        borderRadius: 8,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        minWidth: 48,
                      }}
                    >
                      {i + 1}
                    </button>
                  );
                })}
              </div>
              <div style={{ fontSize: 11, color: '#aaa', marginTop: 6 }}>
                Toggle individual panels on or off
              </div>
            </div>
          )}

          {/* Frame color */}
          {capabilities.hasFrameTexture && (
            <div>
              <SectionLabel>Frame Color</SectionLabel>
              <ColorSwatchGrid
                textures={frameTextureList}
                selected={selectedTextureSecondary}
                onSelect={handleSecondaryTexture}
              />
            </div>
          )}

          {/* Texture / Color */}
          {textureList.length > 0 && (
            <div>
              <SectionLabel>{getTextureTitle()}</SectionLabel>
              <ColorSwatchGrid
                textures={textureList}
                selected={selectedTexture}
                onSelect={handleTexture}
              />
            </div>
          )}

          {/* Bottom spacer */}
          <div style={{ height: 8 }} />
        </div>
      </div>
    </>
  );
};
